import { PDFDocument, StandardFonts, rgb, type PDFFont, type PDFPage } from 'pdf-lib';

const PAGE_WIDTH = 595.28;
const PAGE_HEIGHT = 841.89;
const MARGIN = 48;
const BODY_SIZE = 11;
const HEADING_SIZE = 13;
const TITLE_SIZE = 18;
const LINE_GAP = 3;

export type PatientReportPdfLetterhead =
  | { mode: 'none' }
  | { mode: 'invoice'; lines: readonly string[]; logo?: Uint8Array | null }
  | { mode: 'image'; bytes: Uint8Array }
  | { mode: 'pdf'; bytes: Uint8Array };

export type PatientReportPdfInput = {
  title: string;
  blocks: readonly { heading: string; body: string }[];
  letterhead: PatientReportPdfLetterhead;
  /** Optional cover sheet. Drawn before the report body. Not stored as patient data. */
  cover?: { title: string; body: string } | null;
};

function toWinAnsi(text: string): string {
  return Array.from(text, (ch) => {
    const code = ch.charCodeAt(0);
    return code >= 32 && code <= 255 ? ch : '?';
  }).join('');
}

function wrapLine(text: string, font: PDFFont, size: number, maxWidth: number): string[] {
  const words = toWinAnsi(text).split(/\s+/).filter(Boolean);
  if (words.length === 0) return [''];
  const lines: string[] = [];
  let current = '';
  for (const word of words) {
    const next = current ? `${current} ${word}` : word;
    if (font.widthOfTextAtSize(next, size) <= maxWidth) {
      current = next;
      continue;
    }
    if (current) lines.push(current);
    if (font.widthOfTextAtSize(word, size) <= maxWidth) {
      current = word;
      continue;
    }
    let chunk = '';
    for (const ch of word) {
      const attempt = chunk + ch;
      if (font.widthOfTextAtSize(attempt, size) <= maxWidth) {
        chunk = attempt;
      } else {
        if (chunk) lines.push(chunk);
        chunk = ch;
      }
    }
    current = chunk;
  }
  if (current) lines.push(current);
  return lines.length > 0 ? lines : [''];
}

async function embedRaster(pdf: PDFDocument, bytes: Uint8Array) {
  const png = bytes.length > 8 && bytes[0] === 0x89 && bytes[1] === 0x50;
  if (png) return pdf.embedPng(bytes);
  return pdf.embedJpg(bytes);
}

/** A4 PDF with an optional letterhead and the editable report sections. */
export async function buildPatientReportPdf(input: PatientReportPdfInput): Promise<Uint8Array> {
  const pdf = await PDFDocument.create();
  const font = await pdf.embedFont(StandardFonts.Helvetica);
  const bold = await pdf.embedFont(StandardFonts.HelveticaBold);
  const width = PAGE_WIDTH - MARGIN * 2;

  let page = pdf.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
  let y = PAGE_HEIGHT - MARGIN;

  const ensure = (needed: number) => {
    if (y - needed >= MARGIN) return;
    page = pdf.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
    y = PAGE_HEIGHT - MARGIN;
  };

  const drawLines = (
    lines: string[],
    usedFont: PDFFont,
    size: number,
    color = rgb(0.12, 0.14, 0.16),
  ) => {
    const step = size + LINE_GAP;
    for (const line of lines) {
      ensure(step);
      page.drawText(line, { x: MARGIN, y: y - size, size, font: usedFont, color });
      y -= step;
    }
  };

  y = await drawLetterhead(pdf, page, font, y, input.letterhead);
  page = pdf.getPages()[pdf.getPageCount() - 1];

  if (input.cover && (input.cover.title.trim() || input.cover.body.trim())) {
    if (input.cover.title.trim()) {
      drawLines(wrapLine(input.cover.title.trim(), bold, TITLE_SIZE, width), bold, TITLE_SIZE);
      y -= 8;
    }
    for (const paragraph of input.cover.body.split(/\n/)) {
      drawLines(wrapLine(paragraph, font, BODY_SIZE, width), font, BODY_SIZE);
    }
    page = pdf.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
    y = PAGE_HEIGHT - MARGIN;
  }

  drawLines(wrapLine(input.title || 'Rapport', bold, TITLE_SIZE, width), bold, TITLE_SIZE);
  y -= 8;

  const blocks = input.blocks.filter((block) => block.heading.trim() || block.body.trim());
  if (blocks.length === 0) {
    drawLines(wrapLine(' ', font, BODY_SIZE, width), font, BODY_SIZE);
  }
  for (const block of blocks) {
    if (block.heading.trim()) {
      y -= 6;
      drawLines(wrapLine(block.heading.trim(), bold, HEADING_SIZE, width), bold, HEADING_SIZE);
    }
    const paragraphs = (block.body || '').split(/\n/);
    for (const paragraph of paragraphs) {
      drawLines(wrapLine(paragraph, font, BODY_SIZE, width), font, BODY_SIZE);
    }
  }

  return pdf.save();
}

async function drawLetterhead(
  pdf: PDFDocument,
  page: PDFPage,
  font: PDFFont,
  startY: number,
  letterhead: PatientReportPdfLetterhead,
): Promise<number> {
  if (letterhead.mode === 'none') return startY;
  const width = PAGE_WIDTH - MARGIN * 2;
  let y = startY;

  if (letterhead.mode === 'pdf') {
    try {
      const [embedded] = await pdf.embedPdf(letterhead.bytes, [0]);
      const first = pdf.getPages()[0];
      const scale = Math.min(PAGE_WIDTH / embedded.width, 160 / embedded.height, 1);
      const drawWidth = embedded.width * scale;
      const drawHeight = embedded.height * scale;
      first.drawPage(embedded, {
        x: (PAGE_WIDTH - drawWidth) / 2,
        y: PAGE_HEIGHT - MARGIN - drawHeight,
        width: drawWidth,
        height: drawHeight,
      });
      return PAGE_HEIGHT - MARGIN - drawHeight - 16;
    } catch {
      return startY;
    }
  }

  if (letterhead.mode === 'image' || (letterhead.mode === 'invoice' && letterhead.logo)) {
    const bytes = letterhead.mode === 'image' ? letterhead.bytes : letterhead.logo;
    if (bytes && bytes.length > 0) {
      try {
        const image = await embedRaster(pdf, bytes);
        const maxHeight = letterhead.mode === 'image' ? 120 : 56;
        const scale = Math.min(width / image.width, maxHeight / image.height, 1);
        const drawWidth = image.width * scale;
        const drawHeight = image.height * scale;
        page.drawImage(image, {
          x: MARGIN,
          y: y - drawHeight,
          width: drawWidth,
          height: drawHeight,
        });
        y -= drawHeight + 10;
      } catch {
        // A logo the font engine cannot embed still leaves the text letterhead.
      }
    }
  }

  if (letterhead.mode === 'invoice') {
    for (const line of letterhead.lines) {
      const wrapped = wrapLine(line, font, 10, width);
      for (const part of wrapped) {
        page.drawText(part, {
          x: MARGIN,
          y: y - 10,
          size: 10,
          font,
          color: rgb(0.2, 0.24, 0.28),
        });
        y -= 13;
      }
    }
    y -= 8;
  }

  return y;
}

export function invoiceLetterheadLines(input: {
  name?: string | null;
  address?: unknown;
  cvr?: string | null;
  phone?: string | null;
  email?: string | null;
}): string[] {
  const address = addressRecord(input.address);
  const street = text(address?.street) ?? text(address?.line1);
  const zip = text(address?.zip_code) ?? text(address?.zip) ?? text(address?.postal_code);
  const city = text(address?.city);
  const country = text(address?.country);
  const lines = [
    text(input.name),
    street,
    [zip, city].filter(Boolean).join(' ') || null,
    country,
    input.cvr ? `CVR ${input.cvr}` : null,
    text(input.phone),
    text(input.email),
  ];
  return lines.filter((line): line is string => Boolean(line));
}

function addressRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function text(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}
